(() => {
  const elementName = 'nuora-ingredient-browser';
  if (customElements.get(elementName)) return;

  class NuoraIngredientBrowser extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.track = this.querySelector('[data-nuora-carousel-track]');
      this.cards = Array.from(this.querySelectorAll('[data-ingredient-card]'));
      this.filters = Array.from(this.querySelectorAll('[data-ingredient-filter]'));
      this.dots = Array.from(this.querySelectorAll('[data-ingredient-dot]'));
      this.previousButton = this.querySelector('[data-ingredient-prev]');
      this.nextButton = this.querySelector('[data-ingredient-next]');
      this.activeFilter = this.dataset.initialFilter || 'all';
      this.activeIndex = 0;
      this.scrollFrame = null;
      this.filterFrame = null;
      this.setAttribute('data-nuora-carousel-ready', '');

      this.addEventListener('click', this.handleClick.bind(this));
      this.track?.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
      this.applyFilter(this.activeFilter);
    }

    get visibleCards() {
      return this.cards.filter((card) => !card.hidden);
    }

    handleClick(event) {
      const filterButton = event.target.closest('[data-ingredient-filter]');
      if (filterButton) {
        this.applyFilter(filterButton.dataset.ingredientFilter);
        return;
      }

      if (event.target.closest('[data-ingredient-prev]')) {
        this.goTo(this.activeIndex - 1);
        return;
      }

      if (event.target.closest('[data-ingredient-next]')) {
        this.goTo(this.activeIndex + 1);
        return;
      }

      const dot = event.target.closest('[data-ingredient-dot]');
      if (dot && !dot.hidden) this.goTo(Number(dot.dataset.index) || 0);
    }

    applyFilter(filter) {
      if (!this.filters.some((button) => button.dataset.ingredientFilter === filter)) {
        filter = 'all';
      }
      this.activeFilter = filter;
      if (this.filterFrame) cancelAnimationFrame(this.filterFrame);
      if (this.track) {
        this.track.scrollTo({ left: 0, behavior: 'auto' });
        this.track.scrollLeft = 0;
      }
      this.filters.forEach((button) => {
        const active = button.dataset.ingredientFilter === filter;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });

      this.cards.forEach((card) => {
        const categories = (card.dataset.ingredientCategories || '').split(/\s+/);
        const matches = filter === 'all' || categories.includes(filter) || card.dataset.alwaysVisible === 'true';
        card.hidden = !matches;
      });

      this.activeIndex = 0;
      this.rebuildDots();
      this.setState(0);
      this.filterFrame = requestAnimationFrame(() => {
        this.filterFrame = requestAnimationFrame(() => {
          this.filterFrame = null;
          if (!this.track) return;
          this.track.scrollLeft = 0;
          this.goTo(0, false);
        });
      });
    }

    rebuildDots() {
      const count = this.visibleCards.length;
      this.dots.forEach((dot, index) => {
        dot.hidden = index >= count;
        dot.dataset.index = index;
      });
    }

    handleScroll() {
      if (!this.track || this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        const trackLeft = this.track.getBoundingClientRect().left;
        let index = 0;
        let distance = Number.POSITIVE_INFINITY;
        this.visibleCards.forEach((card, cardIndex) => {
          const cardDistance = Math.abs(card.getBoundingClientRect().left - trackLeft);
          if (cardDistance < distance) {
            distance = cardDistance;
            index = cardIndex;
          }
        });
        this.setState(index);
      });
    }

    setState(index) {
      const cards = this.visibleCards;
      if (!cards.length) return;
      this.activeIndex = Math.max(0, Math.min(index, cards.length - 1));
      this.dots.forEach((dot, dotIndex) => {
        const active = !dot.hidden && dotIndex === this.activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', String(active));
      });
      if (this.previousButton) this.previousButton.disabled = this.activeIndex === 0;
      if (this.nextButton) this.nextButton.disabled = this.activeIndex === cards.length - 1;
    }

    goTo(index, smooth = true) {
      const cards = this.visibleCards;
      if (!cards.length || !this.track) return;
      const loop = this.dataset.loop === 'true';
      let nextIndex = index;
      if (loop) nextIndex = (index + cards.length) % cards.length;
      nextIndex = Math.max(0, Math.min(nextIndex, cards.length - 1));
      this.setState(nextIndex);
      this.track.scrollTo({
        left: cards[nextIndex].offsetLeft,
        behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto',
      });
    }
  }

  customElements.define(elementName, NuoraIngredientBrowser);
})();
